import {
  Archive,
  Bot,
  ChevronLeft,
  ChevronRight,
  Clock,
  Columns3,
  Inbox as InboxIcon,
  MailQuestion,
  PencilLine,
  PenLine,
  Plus,
  Rows3,
  Send,
  Star,
  Tag,
  TriangleAlert,
  UserCheck,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComposeDraftSummary, Conversation, ConversationStatus, Inbox } from '../../api/types';
import {
  channelKeyForInbox,
  channelMetaForInbox,
  CHANNEL_META,
  INBOX_ICONS,
  inboxIconKey,
  type ChannelKey,
} from '../../lib/commsMeta';
import {
  identityLine,
  isManagedInbox,
  needsReauth,
  providerLine,
  reauthSentence,
  staleInboxes,
} from '../../lib/channelMeta';
import { useMailLayout, writeMailLayout, type MailLayout } from '../../lib/mailLayout';
import { matchesComposeDraft, seedComposeDrafts, useComposeDrafts } from './composeDrafts';
import { ContactPanel } from './ContactPanel';
import { ConversationList } from './ConversationList';
import { useComms } from './context';
import { useNotifications } from './notifications';
import { GmailView } from './gmail/GmailView';
import { ThreadPane } from './ThreadPane';
import { useConfigNav } from './useConfigNav';
import { useModuleRefresh } from './useModuleRefresh';
import { RefreshButton } from '../../components/RefreshButton';
import { ShowChannelBar } from './channelBar';
import { useConversations, type ConversationQuery } from './useConversations';

interface ViewDef {
  key: string;
  label: string;
  icon: LucideIcon;
  status: ConversationStatus | 'all';
  assigneeType: 'all' | 'me' | 'unassigned';
  botHandled?: boolean;
  /** The Drafts view: conversations THIS agent left a reply unsent on. */
  hasDraft?: boolean;
  /** The Sent view: conversations anyone on the team has replied to. */
  sent?: boolean;
  /** The Starred view: conversations the team flagged, across every status. */
  starred?: boolean;
  sortBy: ConversationQuery['sortBy'];
  countKey?:
    | 'unassignedCount'
    | 'mineCount'
    | 'allCount'
    | 'pendingCount'
    | 'botHandledCount'
    | 'draftCount';
  attention?: boolean;
  /** Draw a rule ABOVE this view — see the group comment in VIEWS. */
  groupStart?: boolean;
}

/**
 * The view that would list this conversation, for one opened from outside the
 * list. Only ever WIDENS to a view that contains it: a conversation already in
 * the current view leaves the view alone, so an agent who filtered deliberately
 * is not yanked out of it by clicking a row.
 */
const viewHolding = (conversation: Conversation, current: string, followsBot: boolean): string => {
  const view = VIEWS.find((v) => v.key === current);
  // 'All open' really does hold the assistant's conversations for an agent
  // following them (see OPEN_AND_BOT), so it must not send them to Bot queue.
  if (followsBot && view?.status === 'open' && OPEN_AND_BOT.includes(conversation.status)) {
    return current;
  }
  // Same status is enough to stay put: the row may simply be on a later page
  // of a list this long, and switching views under an agent who can already
  // reach it would be the ruder answer.
  if (view && (view.status === 'all' || view.status === conversation.status)) return current;
  // `assigneeType: 'all'` is what makes the target actually hold it — the views
  // that narrow by assignee would drop it again for the half of conversations
  // that are somebody else's.
  const holder = VIEWS.find(
    (v) => v.status === conversation.status && v.assigneeType === 'all' && !v.botHandled,
  );
  return holder?.key ?? current;
};

/** 'All open' for an agent following the assistant: live work, whoever holds it. */
const OPEN_AND_BOT: ConversationStatus[] = ['open', 'pending'];

/**
 * Two groups, and the rule between them is the whole ordering.
 *
 * ABOVE it is the mailbox as a mail client draws it — where a thread IS, by its
 * own state: live, owed an answer, flagged, put off, answered, half-written,
 * closed. An agent arriving at the module reads down that list without knowing
 * anything about the team. BELOW it is the same mailbox cut by WHO has it,
 * which only means something once you know there is a team and a bot: the
 * triage queues. They were interleaved before, so 'Unassigned' — a question
 * about staffing — was the first thing anyone saw.
 *
 * The order inside the first group is a mail client's, deliberately: it is the
 * one ordering every operator already knows, and this rail is read far more
 * often than it is thought about.
 */
const VIEWS: ViewDef[] = [
  {
    // WHAT is listed, never WHERE: the mailbox it is read in is the rail's
    // other half, chosen above (All Inboxes, or one of them). So this says
    // "Inbox" rather than anything about scope, and its count — computed under
    // the same filters as the list, the pin included — is true whichever
    // mailbox is chosen. The key stays `open` so nothing deep-linked changes.
    key: 'open',
    label: 'Inbox',
    icon: InboxIcon,
    status: 'open',
    assigneeType: 'all',
    sortBy: 'latest',
    countKey: 'allCount',
  },
  {
    key: 'waiting',
    label: 'Awaiting reply',
    icon: Clock,
    status: 'open',
    assigneeType: 'all',
    sortBy: 'waiting_since',
    attention: true,
  },
  {
    // Flagged by the team, and OURS — nothing syncs it to the mailbox this
    // channel belongs to, so a star here is invisible in Gmail and a star
    // there is invisible here. Across every status like the rest of this
    // group: a thread flagged and then closed is exactly what somebody
    // flagged it to find. No count badge — a shelf is not a queue.
    key: 'starred',
    label: 'Starred',
    icon: Star,
    status: 'all',
    assigneeType: 'all',
    starred: true,
    sortBy: 'latest',
  },
  {
    key: 'snoozed',
    label: 'Snoozed',
    icon: Clock,
    status: 'snoozed',
    assigneeType: 'all',
    sortBy: 'latest',
  },
  {
    // The other side of 'Awaiting reply' above: what has gone out rather than
    // what is still owed. Everyone's, not this agent's — the mailbox is shared,
    // and "did anyone get back to them" is the question this answers; the
    // assistant's replies count too, because they left on the channel like any
    // other, and 'Handled by chatbot' is what isolates those.
    //
    // Across every status, like a mail client's Sent folder: a thread answered
    // and then closed is exactly the one an agent comes here looking for. No
    // count badge for the same reason Closed has none — an archive's size is
    // not a number of things to do.
    key: 'sent',
    label: 'Sent',
    icon: Send,
    status: 'all',
    assigneeType: 'all',
    sent: true,
    // Ordered by when WE last wrote, not by the thread's last activity: a Sent
    // list that reshuffles when the customer answers is ordered by their day.
    sortBy: 'last_outgoing',
  },
  {
    // Unsent work, which is nobody's queue but this agent's, and counts across
    // every status: a reply half written on a thread that was resolved while it
    // was being written is exactly the one that goes missing.
    key: 'drafts',
    label: 'Drafts',
    icon: PencilLine,
    status: 'all',
    assigneeType: 'all',
    hasDraft: true,
    // Ordered by when the agent last wrote in it, not by when the customer
    // last wrote: this list is read as "what was I in the middle of".
    sortBy: 'draft_updated',
    countKey: 'draftCount',
  },
  {
    key: 'resolved',
    label: 'Closed',
    icon: Archive,
    status: 'resolved',
    assigneeType: 'all',
    sortBy: 'latest',
  },
  {
    key: 'unassigned',
    label: 'Unassigned',
    icon: UserPlus,
    status: 'open',
    assigneeType: 'unassigned',
    sortBy: 'latest',
    countKey: 'unassignedCount',
    attention: true,
    groupStart: true,
  },
  {
    key: 'mine',
    label: 'Assigned to me',
    icon: UserCheck,
    status: 'open',
    assigneeType: 'me',
    sortBy: 'latest',
    countKey: 'mineCount',
  },
  {
    key: 'pending',
    label: 'Bot queue',
    icon: Bot,
    status: 'pending',
    assigneeType: 'all',
    sortBy: 'latest',
    countKey: 'pendingCount',
  },
  {
    // The Bot queue above is what the assistant HOLDS. This is what it has
    // answered — the ones that left that queue the moment it handed off, which
    // are exactly the ones worth reviewing.
    key: 'bot-handled',
    label: 'Handled by chatbot',
    icon: Bot,
    status: 'all',
    assigneeType: 'all',
    botHandled: true,
    sortBy: 'latest',
    countKey: 'botHandledCount',
  },
];

/**
 * Where the module opens, and what an unknown view key falls back to. By key
 * rather than by index: this list is reordered whenever the rail is, and an
 * index would silently start the module on whatever moved into that slot.
 */
const DEFAULT_VIEW = VIEWS.find((view) => view.key === 'open') as ViewDef;

/**
 * "These channels have stopped" as one line above the mailbox list.
 *
 * The rows below carry their own marker, but a marker is only readable while
 * the agent is looking at the rail — and folded to icons it is a triangle nine
 * pixels wide. This says it in words, and, for whoever can act on it, is the
 * way to the screen that reconnects.
 *
 * `onFix` null renders a plain strip rather than a dead button: an agent below
 * Manage is being told why their replies are failing, not offered a fix the
 * server would refuse.
 */
function StaleChannelsStrip({
  open,
  stale,
  onFix,
}: {
  open: boolean;
  stale: Inbox[];
  onFix: (() => void) | null;
}) {
  const label =
    stale.length === 1
      ? `${identityLine(stale[0])} needs reconnecting`
      : `${stale.length} channels need reconnecting`;
  const title =
    stale.length === 1
      ? reauthSentence(stale[0])
      : `${stale.length} channels lost their authorization and cannot send or receive: ${stale
          .map(identityLine)
          .join(', ')}.`;
  const body = (
    <>
      <TriangleAlert size={13} />
      {open && <span className="cv-rail-stale-text">{label}</span>}
    </>
  );
  const className = `cv-rail-stale${open ? '' : ' compact'}`;
  if (!onFix) {
    return (
      <div className={className} title={title} role="status">
        {body}
      </div>
    );
  }
  return (
    <button type="button" className={className} title={title} onClick={onFix}>
      {body}
    </button>
  );
}

function Rail({
  open,
  onToggle,
  accounts,
  unreadByInbox,
  selectedInboxId,
  onSelectInbox,
  viewKey,
  onSelectView,
  counts,
  labels,
  selectedLabels,
  onToggleLabel,
  onCompose,
  composeDenied,
  layout,
  onLayout,
}: {
  open: boolean;
  onToggle: () => void;
  accounts: Inbox[];
  unreadByInbox: Map<number, number>;
  selectedInboxId: number | null;
  onSelectInbox: (id: number | null) => void;
  viewKey: string;
  onSelectView: (view: ViewDef) => void;
  counts: Partial<Record<NonNullable<ViewDef['countKey']>, number>>;
  /** The tenant's label catalog — the rail lists it, nothing here creates one. */
  labels: string[];
  selectedLabels: string[];
  onToggleLabel: (title: string) => void;
  /** Open a blank composer. Owned by the module, which renders the panel. */
  onCompose: () => void;
  composeDenied: boolean;
  /**
   * How this agent reads mail, or null on every tab but Email — the mail-client
   * shape is a shape for MAIL, and offering it over a list of SMS threads would
   * be a control that changes nothing anyone can see.
   */
  layout: MailLayout | null;
  onLayout: (next: MailLayout) => void;
}) {
  const openConfig = useConfigNav();
  const { refreshAll, refreshingAll } = useComms();
  const stale = useMemo(() => staleInboxes(accounts), [accounts]);
  // Only an agent who MANAGES one of them has anywhere to be sent: below Manage
  // the settings screen answers "you do not manage this channel", and a strip
  // that opened a tab to say so is worse than one that does not open.
  const fixable = stale.some(isManagedInbox);
  const showingAll = selectedInboxId === null;
  // Unread is counted off the loaded rows, which a pin narrows to one mailbox —
  // so pinned, this sum is that mailbox's alone, and beside the words "All
  // Inboxes" it would be the wrong number. Shown only while it is the truth.
  const allUnread = showingAll ? [...unreadByInbox.values()].reduce((sum, n) => sum + n, 0) : 0;
  return (
    <div className={`cv-rail ${open ? 'open' : 'closed'}`}>
      {/* Above the head label rather than in it: while the channel bar is
          folded this is the only way back to it, and a row of its own is what
          says so. It renders nothing at all when the bar is showing. */}
      <ShowChannelBar compact={!open} />
      {/* Above everything, where a mail client puts it. It used to live in the
          channel bar, which FOLDS — so the one action that starts work
          disappeared with it, on exactly the embedded layout that folds it by
          default. Here it is on screen whenever the inbox is, and it collapses
          to its icon with the rest of the rail. */}
      <button
        type="button"
        className="cv-rail-compose"
        disabled={composeDenied}
        title={composeDenied ? 'Your role cannot send' : 'Compose a new message'}
        onClick={onCompose}
      >
        <PenLine size={15} />
        {open && <span className="cv-rail-compose-label">Compose</span>}
      </button>
      <div className="cv-rail-head">
        {open && <div className="cv-rail-head-label">MAILBOXES &amp; NUMBERS</div>}
        {/* The whole module, not this list: a channel connected in another tab
            appears in this rail only once the inboxes are re-read, which is the
            question an agent is actually asking when they press refresh here.
            It sits in the rail rather than over the list because the channel bar
            — where the module's other Refresh lives — folds away, and a reload
            you have to unfold something to reach is one you do not have. */}
        <RefreshButton
          className="cv-rail-toggle cv-rail-refresh"
          size={14}
          label={null}
          title="Refresh Communications"
          refreshing={refreshingAll}
          onClick={refreshAll}
        />
        <button
          type="button"
          className="cv-rail-toggle"
          onClick={onToggle}
          title={open ? 'Collapse rail' : 'Expand rail'}
        >
          {open ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>
      {stale.length > 0 && (
        <StaleChannelsStrip
          open={open}
          stale={stale}
          onFix={fixable ? () => openConfig(stale.length === 1 ? `/inboxes/${stale[0].id}` : '/') : null}
        />
      )}
      {/* The way out of a pin, at the head of the list it is a choice among —
          where a mail client puts its combined inbox. It used to be a VIEW
          below, paired with a "Current Inbox" view, and the pair made one rail
          answer two questions with one highlight: which mailbox, and which
          slice of it. Here the mailbox rows hold exactly one highlight (this,
          or the pinned mailbox) and the views hold the other. */}
      {accounts.length > 0 && (
        <button
          type="button"
          className={`cv-account cv-account-all${showingAll ? ' active' : ''}`}
          title="Every mailbox and number below"
          onClick={() => onSelectInbox(null)}
        >
          <span className="cv-account-chip cv-account-all-chip">
            <InboxIcon size={13} />
            {!open && allUnread > 0 && <span className="cv-account-dot" />}
          </span>
          {open && (
            <>
              <span className="cv-account-main">
                <span className="cv-account-key">All Inboxes</span>
                <span className="cv-account-sub">
                  {accounts.length === 1 ? '1 channel' : `${accounts.length} channels`}
                </span>
              </span>
              {allUnread > 0 && <span className="cv-account-count">{allUnread}</span>}
            </>
          )}
        </button>
      )}
      {accounts.map((inbox) => {
        const meta = channelMetaForInbox(inbox);
        const Icon = INBOX_ICONS[inboxIconKey(inbox)];
        const unread = unreadByInbox.get(inbox.id) ?? 0;
        const active = selectedInboxId === inbox.id;
        // A channel that lost its authorization is still listed and still
        // readable — everything already in it is there. What changes is that
        // nothing new arrives and nothing sent leaves, which is why the marker
        // rides the row rather than removing it.
        const authLapsed = needsReauth(inbox);
        return (
          <button
            key={inbox.id}
            type="button"
            className={`cv-account${active ? ' active' : ''}${authLapsed ? ' stale' : ''}`}
            title={authLapsed ? reauthSentence(inbox) : identityLine(inbox)}
            onClick={() => onSelectInbox(active ? null : inbox.id)}
          >
            <span className="cv-account-chip" style={{ background: meta.muted }}>
              <Icon size={13} style={{ color: meta.color }} />
              {!open && unread > 0 && <span className="cv-account-dot" />}
              {/* Beats the unread dot to the corner on purpose: unread is a
                  number the agent can still act on, a dead channel is not. */}
              {authLapsed && (
                <span className="cv-account-warn">
                  <TriangleAlert size={9} />
                </span>
              )}
            </span>
            {open && (
              <>
                <span className="cv-account-main">
                  <span className="cv-account-key">{identityLine(inbox)}</span>
                  {/* The provider's name is decoration next to this: a row 200px
                      wide holds one sub-line, and "which vendor" is never the
                      thing an agent needs off a channel that has stopped. */}
                  <span className={`cv-account-sub${authLapsed ? ' stale' : ''}`}>
                    {authLapsed ? 'Reconnect needed' : providerLine(inbox)}
                  </span>
                </span>
                {unread > 0 && <span className="cv-account-count">{unread}</span>}
              </>
            )}
          </button>
        );
      })}
      <div className="cv-rail-divider" />
      {open && <div className="cv-rail-views-label">VIEWS</div>}
      {VIEWS.map((view) => {
        const Icon = view.icon;
        const count = view.countKey ? counts[view.countKey] : undefined;
        const button = (
          <button
            key={view.key}
            type="button"
            className={`cv-view${viewKey === view.key ? ' active' : ''}`}
            title={view.label}
            onClick={() => onSelectView(view)}
          >
            <Icon size={14} />
            {open && (
              <>
                <span className="cv-view-label">{view.label}</span>
                {count !== undefined && count > 0 && (
                  <span className={`cv-view-count${view.attention ? ' attention' : ''}`}>
                    {count}
                  </span>
                )}
              </>
            )}
          </button>
        );
        // The rule between "where a thread is" and "who has it" — see VIEWS. It
        // is drawn from the data rather than by splitting the list in two, so
        // moving a view between the groups stays a one-line edit up there.
        return view.groupStart ? (
          <Fragment key={view.key}>
            <div className="cv-rail-divider cv-rail-group" />
            {button}
          </Fragment>
        ) : (
          button
        );
      })}
      {/* The labels the tenant has made, under the views a mail client would
          put them under. Cutting the list by one is what they are FOR — a
          filter beside the views rather than a chip on a row, so it survives
          moving between views the way the mailbox pin does.

          Several at once means ANY of them, which is what the server answers.
          Folded, only the ones actually filtering are drawn: a rail with no
          room for names would otherwise hide the reason the list is short,
          which reads as an inbox that has lost half its threads. */}
      {(open ? labels.length > 0 : selectedLabels.length > 0) && (
        <>
          <div className="cv-rail-divider cv-rail-group" />
          {open && <div className="cv-rail-views-label">LABELS</div>}
          {(open ? labels : selectedLabels).map((title) => {
            const active = selectedLabels.includes(title);
            return (
              <button
                key={title}
                type="button"
                className={`cv-view${active ? ' active' : ''}`}
                title={active ? `Stop filtering by ${title}` : `Only conversations labelled ${title}`}
                onClick={() => onToggleLabel(title)}
              >
                <Tag size={14} />
                {open && <span className="cv-view-label">{title}</span>}
              </button>
            );
          })}
        </>
      )}
      <div className="cv-rail-fill" />
      {/* Under the views and above the one button that leaves this module: it
          is a setting ABOUT the list rather than a place to go, and the foot of
          the rail is the only spot that reads as such. Absent on every other
          tab — see the prop. */}
      {layout && (
        <div className="cv-rail-layout" role="group" aria-label="Email layout">
          {open ? (
            <>
              <div className="cv-rail-layout-label">LAYOUT</div>
              <div className="cv-layout-switch">
                <button
                  type="button"
                  className={`cv-layout-opt${layout === 'crm' ? ' on' : ''}`}
                  title="Three panes: the list beside the thread"
                  onClick={() => onLayout('crm')}
                >
                  <Columns3 size={13} />
                  CRM
                </button>
                <button
                  type="button"
                  className={`cv-layout-opt${layout === 'gmail' ? ' on' : ''}`}
                  title="One full-width list, then the thread — like a mail client"
                  onClick={() => onLayout('gmail')}
                >
                  <Rows3 size={13} />
                  Mail
                </button>
              </div>
            </>
          ) : (
            // Folded, there is no room for two of anything, so the one button
            // shows what is ON and switches to the other.
            <button
              type="button"
              className="cv-layout-opt on collapsed"
              title={layout === 'crm' ? 'Switch to the mail layout' : 'Switch to the CRM layout'}
              onClick={() => onLayout(layout === 'crm' ? 'gmail' : 'crm')}
            >
              {layout === 'crm' ? <Columns3 size={14} /> : <Rows3 size={14} />}
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className="cv-rail-add"
        title="Add mailbox or number"
        onClick={() => openConfig('/connect')}
      >
        <Plus size={14} />
        {open && <span className="cv-rail-add-label">Add mailbox or number</span>}
      </button>
    </div>
  );
}

/** Inbox tab body: accounts/views rail · conversation list · thread · context. */
export function InboxView({
  channelKey,
  selectedId,
  onSelectId,
  onCompose,
  onOpenComposeDraft,
}: {
  channelKey: ChannelKey;
  /** Selected conversation display id (owned by the module's URL state). */
  selectedId: number | null;
  onSelectId: (id: number | null) => void;
  /** Open a blank composer — the panel itself is the module's, not this view's. */
  onCompose: () => void;
  /**
   * Reopen an unsent new message into that same panel. It belongs to the
   * module for the same reason the blank one does: the compose window is docked
   * over the whole of Communications and outlives a tab switch, which is the
   * only reason an agent can look something up while writing.
   */
  onOpenComposeDraft: (draft: ComposeDraftSummary) => void;
}) {
  const { source, inboxes, agents, labels, me, sendDenied, draftScope } = useComms();

  const channelInboxes = useMemo(
    () =>
      channelKey === 'all'
        ? inboxes
        : inboxes.filter((inbox) => channelKeyForInbox(inbox) === channelKey),
    [inboxes, channelKey],
  );

  /**
   * The shape the Email tab is drawn in, per agent per browser. Offered on that
   * tab alone: 'all' mixes six channels into one list, and a mail client's
   * sender/subject/snippet line has nothing to say about a phone call — so
   * every other tab reads as 'crm' whatever this agent last chose for mail.
   */
  const savedLayout = useMailLayout(draftScope);
  const mailLayout: MailLayout = channelKey === 'email' ? savedLayout : 'crm';

  const [viewKey, setViewKey] = useState(DEFAULT_VIEW.key);
  const view = VIEWS.find((v) => v.key === viewKey) ?? DEFAULT_VIEW;
  const [assigneeType, setAssigneeType] = useState<ConversationQuery['assigneeType']>('all');
  const [sortBy, setSortBy] = useState<ConversationQuery['sortBy']>('latest');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [unreadFirst, setUnreadFirst] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  const [listWidth, setListWidth] = useState(352);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [inboxId, setInboxId] = useState<number | null>(null);
  /**
   * The pin, readable from an effect that must not DEPEND on it — the deep-link
   * fetch below has already answered by the time it matters, and re-running it
   * every time a mailbox is clicked would refetch for nothing. Synced in an
   * effect, the same idiom as useConversations' queryRef.
   */
  const pinRef = useRef(inboxId);
  /**
   * The rail's label filter, several meaning ANY of them. Orthogonal to the
   * view the way the mailbox pin is — an agent who filtered to "airport" and
   * then opened Resolved means airport threads that are resolved.
   */
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [searchAll, setSearchAll] = useState(false);
  const [selected, setSelected] = useState<Conversation | null>(null);
  /**
   * The selection whose place in the rail is settled — it was on the list when
   * it was opened, or it has already been followed to the view that holds it.
   */
  const placedRef = useRef<number | null>(null);
  /**
   * A conversation a rail choice has just closed, until the URL says so too.
   * `?c=` is dropped through the router, which commits in a transition — a beat
   * after the pin or view the same click moved. In that beat the new list can
   * paint from cache, and with the old id still in the URL the selection effect
   * would find it there and open the thread again for a frame.
   */
  const closingRef = useRef<number | null>(null);
  /**
   * "Follow the assistant's conversations" is not only about notifications: an
   * agent who asked to follow that work wants to SEE it in the queue they work
   * from. Without this the notification arrives and the conversation is in one
   * view they were not looking at, which reads as a conversation that does not
   * exist. Only 'All open' widens — the narrower views keep their meaning, and
   * 'unassigned' in particular still means nobody has it.
   */
  const followsBot = useNotifications().preferences?.follow_bot_chats === true;

  useEffect(() => {
    pinRef.current = inboxId;
  });

  // Debounced search → server-side `q`.
  useEffect(() => {
    const timer = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [qInput]);

  // Clearing the box drops the widened scope with it — the view chips have to
  // mean what they say again the moment the search ends.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!q) setSearchAll(false);
  }, [q]);

  // Switching channel tabs drops an inbox pin that no longer applies.
  useEffect(() => {
    // Sync reset against the new tab's inbox set — deliberate.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInboxId((current) =>
      current !== null && channelInboxes.some((inbox) => inbox.id === current) ? current : null,
    );
  }, [channelInboxes]);

  /**
   * What the rail lists. A title being filtered on is folded in even if the
   * catalog no longer carries it — a filter with no row to switch it off is a
   * short list with no visible reason.
   */
  const labelTitles = useMemo(() => {
    const titles = new Set(labels.map((label) => label.title));
    for (const title of labelFilter) titles.add(title);
    return [...titles].sort((a, b) => a.localeCompare(b));
  }, [labels, labelFilter]);

  const inboxIds = useMemo(() => {
    if (inboxId !== null) return [inboxId];
    if (channelKey === 'all') return [];
    return channelInboxes.map((inbox) => inbox.id);
  }, [inboxId, channelKey, channelInboxes]);

  // A widened search leaves the triage view behind on both axes — a contact
  // whose only thread is closed, or assigned to someone else, is exactly who
  // the agent is failing to find.
  const query = useMemo<ConversationQuery>(
    () => ({
      status: searchAll ? 'all' : followsBot && view.status === 'open' ? OPEN_AND_BOT : view.status,
      inboxIds,
      assigneeType: searchAll ? 'all' : assigneeType,
      // Dropped by a widened search for the same reason as the others: the
      // thread being hunted for may not be one the assistant ever touched.
      botHandled: searchAll ? undefined : view.botHandled,
      // A widened search drops it with the rest: the thread being hunted for is
      // rarely one this agent happens to have a draft on.
      hasDraft: searchAll ? undefined : view.hasDraft,
      // Dropped by a widened search too — a thread nobody has answered yet is
      // one of the likelier things to be hunting for.
      sent: searchAll ? undefined : view.sent,
      // And dropped by it as well: a thread being hunted for is rarely one
      // somebody already thought to flag.
      starred: searchAll ? undefined : view.starred,
      // Dropped by a widened search with the rest of them — a thread nobody
      // got round to labelling is exactly the one somebody is hunting for.
      labels: searchAll || !labelFilter.length ? undefined : labelFilter,
      q,
      sortBy,
    }),
    [
      searchAll,
      followsBot,
      view.status,
      view.botHandled,
      view.hasDraft,
      view.sent,
      view.starred,
      inboxIds,
      assigneeType,
      labelFilter,
      q,
      sortBy,
    ],
  );

  const list = useConversations(source, query, me?.dashBoardId ?? null);

  /**
   * The unsent new messages that belong in the Drafts view beside the unsent
   * replies. Read when that view is entered — they change only when this agent
   * writes one, and the compose panel keeps the store live in the meantime, so
   * there is nothing to poll.
   *
   * The list is not narrowed to the channel tab. A draft is unfinished work, and
   * an agent who filtered to Email to answer mail is not thereby saying they
   * have stopped caring about the text they started an hour ago; the row names
   * its own account, which is the honest way to show it.
   */
  const allComposeDrafts = useComposeDrafts();
  // A widened search leaves the Drafts view behind entirely, so its other half
  // goes with it. A search WITHIN the view narrows instead of hiding: there is
  // no server-side search for these, and dropping them the moment anybody types
  // hides exactly the unsent message they are hunting for.
  const showComposeDrafts = view.hasDraft === true && !searchAll;
  const composeDrafts = useMemo(
    () => (q ? allComposeDrafts.filter((draft) => matchesComposeDraft(draft, q)) : allComposeDrafts),
    [allComposeDrafts, q],
  );
  const loadComposeDrafts = useCallback(() => {
    source.api.listComposeDrafts().then(seedComposeDrafts, () => {
      // The conversation half of the view still loads, and the read is retried
      // the next time this view is opened. A failure here must not wipe drafts
      // the compose panel has already put in the store.
    });
  }, [source]);
  useEffect(() => {
    if (showComposeDrafts) loadComposeDrafts();
  }, [showComposeDrafts, loadComposeDrafts]);

  // Selection: prefer the live list row; deep links fetch the row directly.
  useEffect(() => {
    // Closed already, by a rail choice the URL has not caught up with — see
    // closingRef. Answering from the list here is what would reopen it.
    if (selectedId !== null && selectedId === closingRef.current) return;
    // The URL has moved on — to nothing, or straight to another thread — so the
    // close it was catching up with is over. Left set, it would refuse the next
    // notification or deep link that opened that same thread again.
    closingRef.current = null;
    if (selectedId === null) {
      placedRef.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelected(null);
      return;
    }
    const match = list.rows.find((row) => row.id === selectedId);
    if (match) {
      placedRef.current = selectedId;
      setSelected(match);
      return;
    }
    // Only a thread that has not been placed yet is followed to another view.
    // This effect also runs when the LIST moves under a thread already open —
    // the agent picked another view, pinned a mailbox, a reload or a socket
    // update dropped the row — and following then answered the agent's own
    // click by snapping the rail back to the Inbox a beat later, once the
    // fetch landed.
    const follow = placedRef.current !== selectedId;
    let cancelled = false;
    source.api.getConversation(selectedId).then(
      (conversation) => {
        if (cancelled) return;
        setSelected(conversation);
        if (!follow) return;
        placedRef.current = selectedId;
        // A pinned mailbox is a harder filter than any view: a conversation in
        // another one cannot be listed at all while it stands, so the pin goes
        // before the view is even considered. Same reasoning as below — a
        // thread with no row beside it has no way back once it is clicked away.
        // Read through a ref rather than a dependency: the pin changing must not
        // re-run a fetch that has already answered.
        if (pinRef.current !== null && pinRef.current !== conversation.inboxId) setInboxId(null);
        // It opened from somewhere outside this list — a notification, a deep
        // link, the dashboard — and the view it is in does not contain it, so
        // the thread would sit there with no row beside it and no way back to
        // it once the agent clicks away. Follow it to the view it lives in.
        // Loudest for the assistant's own conversations, which are 'pending'
        // and therefore in none of the open views at all.
        setViewKey((current) => viewHolding(conversation, current, followsBot));
      },
      () => {
        if (!cancelled) setSelected(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedId, list.rows, source, followsBot]);

  // The module-wide Refresh reaches the list through here; the open thread and
  // the contact panel answer it themselves.
  useModuleRefresh(list.reload);
  // The Drafts view's other half. A refresh that reloaded only the conversation
  // rows would leave the unsent new messages beside them a version behind.
  useModuleRefresh(() => {
    if (showComposeDrafts) loadComposeDrafts();
  });

  const unreadByInbox = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of list.rows) {
      const unread = row.unreadCount ?? 0;
      if (unread > 0) map.set(row.inboxId, (map.get(row.inboxId) ?? 0) + unread);
    }
    return map;
  }, [list.rows]);

  const inboxById = useMemo(() => new Map(inboxes.map((inbox) => [inbox.id, inbox])), [inboxes]);

  const selectConversation = (conversation: Conversation) => {
    closingRef.current = null;
    onSelectId(conversation.id);
    setSelected(conversation);
    if ((conversation.unreadCount ?? 0) > 0) {
      list.markSeenLocal(conversation.id);
      source.api.markConversationSeen(conversation.id).catch(() => {});
    }
  };

  /**
   * Put the open thread away because a choice in the rail moved the list out
   * from under it. Dropped from the URL the way Back drops it, so a reload — or
   * the dashboard framing the module again — does not bring it back; and from
   * the view at once, so the list is on screen in the frame the rail moves.
   */
  const closeThread = () => {
    if (selectedId === null) return;
    closingRef.current = selectedId;
    setSelected(null);
    onSelectId(null);
  };

  /**
   * Whether the list a pin draws can hold this conversation: one mailbox, or
   * every mailbox on this tab — which is not every one there is, since the
   * contact panel opens a thread on another channel without changing tabs.
   */
  const pinHolds = (pin: number | null, conversation: Conversation) =>
    pin === null
      ? channelInboxes.some((inbox) => inbox.id === conversation.inboxId)
      : conversation.inboxId === pin;

  /**
   * A mailbox picked in the rail, or All Inboxes, which drops the pin.
   *
   * The view is left exactly as it was: an agent in Starred who pins a mailbox
   * meant "Starred, in this mailbox", and yanking them onto the open list would
   * answer a question they did not ask.
   *
   * The open thread is not. In the mail layout it goes whatever was picked: the
   * reading view covers the list, so a mailbox picked while reading is a list
   * the agent asked to see, and leaving the mail up answered the click with a
   * highlight and nothing else. In three panes it goes when the new list cannot
   * hold it — a thread from one account left open beside another account's list
   * reads as that account's, and a reply typed into it leaves from the mailbox
   * the agent has just stepped out of. One the new list still holds stays, its
   * row right there beside it.
   */
  const pinMailbox = (next: number | null) => {
    setInboxId(next);
    if (mailLayout === 'gmail' || (selected !== null && !pinHolds(next, selected))) closeThread();
  };

  // What "mark all read" will cover, said in the words the agent is looking at:
  // the pinned mailbox, else the channel tab, else everything they can read.
  const markAllScope =
    inboxId !== null
      ? (inboxById.get(inboxId)?.name ?? 'this mailbox')
      : channelKey === 'all'
        ? 'every channel you can see'
        : CHANNEL_META[channelKey].label;

  const [markingSeen, setMarkingSeen] = useState(false);
  /**
   * `agentLastSeenAt` is one shared column, so this clears the badge for every
   * agent in the tenant — the same thing opening a conversation already does,
   * but a hundred at a time and with no way back. The list confirms first, and
   * names the scope rather than asking "are you sure".
   */
  const markAllSeen = async () => {
    if (markingSeen) return;
    setMarkingSeen(true);
    list.markAllSeenLocal(inboxIds);
    try {
      // The server bounds one call so a click cannot queue an unbounded burst of
      // broadcasts; a long backlog finishes over several rounds. Capped so a
      // server that kept answering `more` could not spin here forever.
      for (let round = 0; round < 20; round++) {
        const result = await source.api.markAllConversationsSeen(inboxIds);
        if (!result.more || result.count === 0) break;
      }
    } catch {
      // The badges were painted read optimistically — reload rather than leave
      // the list claiming a state the server never reached.
      list.reload();
    } finally {
      setMarkingSeen(false);
    }
  };

  return (
    <div className="cv">
      <Rail
        open={railOpen}
        onToggle={() => setRailOpen(!railOpen)}
        accounts={channelInboxes}
        unreadByInbox={unreadByInbox}
        selectedInboxId={inboxId}
        onSelectInbox={pinMailbox}
        viewKey={viewKey}
        onSelectView={(next) => {
          setViewKey(next.key);
          setAssigneeType(next.assigneeType);
          setSortBy(next.sortBy);
          // A view picked while reading in the mail layout is a list to see,
          // for the reason a mailbox is (see pinMailbox). Three panes keep the
          // thread: a view changes the list beside it in place, and never which
          // account the thread is answered from.
          if (mailLayout === 'gmail') closeThread();
        }}
        counts={list.meta ?? {}}
        labels={labelTitles}
        selectedLabels={labelFilter}
        onToggleLabel={(title) => {
          setLabelFilter((current) =>
            current.includes(title) ? current.filter((one) => one !== title) : [...current, title],
          );
          // The same as a view, for the same reason.
          if (mailLayout === 'gmail') closeThread();
        }}
        onCompose={onCompose}
        composeDenied={sendDenied}
        layout={channelKey === 'email' ? savedLayout : null}
        onLayout={(next) => writeMailLayout(draftScope, next)}
      />
      {mailLayout === 'gmail' ? (
        <GmailView
          list={list}
          composeDrafts={showComposeDrafts ? composeDrafts : []}
          onOpenComposeDraft={onOpenComposeDraft}
          inboxById={inboxById}
          agents={agents}
          selected={selected}
          onSelect={selectConversation}
          onSelectId={onSelectId}
          search={qInput}
          // A mail client's search box searches the mailbox rather than the
          // label you happen to be standing in, so typing here widens exactly
          // as the three-pane layout's "search everything" chip does — and
          // clearing it drops the widening again, in the effect above.
          onSearch={(next) => {
            setQInput(next);
            if (next.trim()) setSearchAll(true);
          }}
        />
      ) : (
        <>
          <ConversationList
            rows={list.rows}
            composeDrafts={showComposeDrafts ? composeDrafts : []}
            onOpenComposeDraft={onOpenComposeDraft}
            inboxById={inboxById}
            agents={agents}
            selectedId={selectedId}
            onSelect={selectConversation}
            controls={{
              q: qInput,
              setQ: setQInput,
              unreadFirst,
              setUnreadFirst,
              sortBy,
              setSortBy,
              assigneeType,
              setAssigneeType,
              reload: list.reload,
              markAllSeen,
              markingSeen,
              markAllScope,
              scopeLabel: view.label,
              searchAll,
              setSearchAll,
            }}
            loading={list.loading}
            stale={list.stale}
            refreshing={list.refreshing}
            error={list.error?.message ?? null}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            width={listWidth}
            onResize={setListWidth}
            mail={channelKey === 'email'}
          />
          {selected ? (
            <>
              <ThreadPane
                conversation={selected}
                inbox={inboxById.get(selected.inboxId)}
                onConversationPatched={list.reload}
                // Clear the selection FIRST: the pane is rendered from `selected`,
                // and a reload that answered before the deselect would repaint a
                // thread that no longer answers a read.
                onDeleted={() => {
                  onSelectId(null);
                  setSelected(null);
                  list.reload();
                }}
                // Marking it unread closes it for the same reason: the pane
                // reads what it shows, so a thread left open would be read
                // again the moment anything re-opened it.
                onClosed={() => {
                  onSelectId(null);
                  setSelected(null);
                  list.reload();
                }}
                onToggleContext={() => setCtxOpen(!ctxOpen)}
                contextOpen={ctxOpen}
              />
              {ctxOpen && (
                <ContactPanel
                  conversation={selected}
                  onClose={() => setCtxOpen(false)}
                  onOpenConversation={(displayId) => onSelectId(displayId)}
                />
              )}
            </>
          ) : (
            <div className="thr">
              <div className="comms-locked" style={{ background: 'var(--c-bg)' }}>
                <div className="comms-locked-inner">
                  <div className="comms-locked-icon">
                    <MailQuestion size={28} />
                  </div>
                  <div className="comms-locked-title">Pick a conversation</div>
                  <div className="comms-locked-body">
                    Choose a thread from the list — or start one with <b>Compose</b>.
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
